import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Loader2, ExternalLink, Check, AlertTriangle, X, Clock, RotateCcw,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { MmCoinIcon } from '../../context/AuthContext';
/* Sayı biçimi TEK YERDEN — çekim paneli de aynı fonksiyonu kullanıyor. */
import { fmtFull } from './ItemVisuals';
/* 🔪 Geri butonu, logo ve YATIRMA MODU DEFTERİ tek kaynaktan.
   Defterin ne işe yaradığı Mm2Brand.jsx'te uzun uzun yazıyor:
   özetle bu panel BAKİYE rayında çalışıyor ve coinflip sayfası
   bunu ayırt edebilsin diye modu kaydediyoruz. */
import { BackButton, rememberDepositMode } from './Mm2Brand';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

/* ============================================================================
   MM2 YATIRMA — eşya ver, MM Coin al
============================================================================ */

const RBX_RE = /^[A-Za-z0-9_]{3,20}$/;

const mmss = (s) => {
  const n = Math.max(0, Math.floor(s));
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`;
};

/* Etkin kur — `effective_rate_bp` ÇARPAN DAHİL gelir.
   ⚠️ `rate_bp` TEK BAŞINA KULLANILMAZ: ham değer önce MM2_VALUE_MULTIPLIER
   ile ölçekleniyor. Sadece rate_bp gösterilirse arayüz "1:1" yazar, kullanıcı
   20 katını alır — güven kıran türden bir tutarsızlık. */
const rateLabel = (cfg) => {
  const bp = Number(cfg?.balance_rail?.effective_rate_bp) || 0;
  if (!bp) return null;
  const x = bp / 10000;
  return x >= 1
    ? `${x.toLocaleString(undefined, { maximumFractionDigits: 2 })}×`
    : `${x.toFixed(3).replace(/0+$/, '')}×`;
};

/* ⚠️ Kalan süre HER SANİYE yeniden hesaplanır, sayaçtan düşülmez: sekme arka
   plana alındığında tarayıcı setInterval'i kısıyor ve düşen sayaç donuyor. */
const useCountdown = (deadline) => {
  const [left, setLeft] = useState(null);
  useEffect(() => {
    if (!deadline) { setLeft(null); return undefined; }
    const tick = () => setLeft(Math.max(0,
      Math.floor((new Date(deadline).getTime() - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [deadline]);
  return left;
};

/* ------------------------------------------------------- KULLANICI ADI */
/* ⚠️ ALAN DOĞRUDAN YAZILABİLİR — çekim panelindeki "Deliver to" ile AYNI.
   Eskiden salt-okunur başlıyordu ve düzenlemek için önce "Change"e basmak
   gerekiyordu. Gerekçe yanlış hesaba eşya göndermeyi zorlaştırmaktı, ama
   iki panel iki farklı davranış gösterince kullanıcı çekimde öğrendiği
   alışkanlığı yatırmada uygulayıp alanı DÜZENLENEMEZ sanıyordu — kilit
   koruma değil, engel oluyordu.

   ⚠️ KORUMA KALDIRILMADI, GÖRÜNÜR HÂLE GETİRİLDİ. Bağlı hesaptan sapıldığı
   anda başlıkta kehribar bir rozet, altta da eşyaların gideceği ad yazıyor.
   Sessizce kilitlenmiş bir alan yerine okunan bir uyarı; çekim paneli de
   riski tam olarak böyle karşılıyor. */
const AccountField = ({ value, onChange, defaultName, disabled }) => {
  const valid = RBX_RE.test(value.trim());
  const changed = value.trim().toLowerCase() !== String(defaultName || '').trim().toLowerCase();

  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-[11px] uppercase tracking-wider text-zinc-500 font-black">
          Trading account
        </span>
        {valid && changed && (
          <span className="text-[10px] font-bold text-[#F5A524]">
            not your linked account
          </span>
        )}
      </div>

      <input
        value={value} disabled={disabled}
        data-testid="mm2-dep-username"
        onChange={(e) => onChange(e.target.value.replace(/[^A-Za-z0-9_]/g, '').slice(0, 20))}
        placeholder="Roblox username"
        spellCheck={false} autoComplete="off" aria-label="Roblox username"
        className={`mm-focus w-full px-4 py-3.5 rounded-xl bg-[#15171a] text-white font-bold
          outline-none border transition-colors placeholder:text-zinc-600
          placeholder:font-medium disabled:opacity-50 disabled:cursor-not-allowed
          ${valid ? 'border-[#2A2D31] focus:border-[#B059E8]'
                  : 'border-[#F5A524]/50 focus:border-[#F5A524]'}`}
      />

      {!valid ? (
        <p className="mt-2 text-[11px] text-[#F5A524] font-semibold">
          3–20 characters — letters, numbers and _ only.
        </p>
      ) : changed ? (
        <p className="mt-2 text-[11px] text-amber-300">
          Items go to <strong className="text-white">{value.trim()}</strong>. Check the spelling.
        </p>
      ) : (
        <p className="mt-2 text-[11px] text-zinc-500">
          You trade from this account. Type a different one to use another.
        </p>
      )}
    </div>
  );
};

/* -------------------------------------------------------------- ADIMLAR */
/* ⚠️ TAKASI KULLANICI AÇAR, BOT DEĞİL. Önceki metin "the bot sends you a
   trade" diyordu ve yanlıştı: bot gelen isteği kabul ediyor. Kullanıcı
   beklerse hiçbir şey olmuyor ve süre boşa doluyor. */
const STEPS = [
  'Join the bot\'s server',
  'Send the bot a trade with your items',
  'Bot accepts — balance updates instantly',
];

const StepList = ({ active }) => (
  <ol className="space-y-2">
    {STEPS.map((label, i) => {
      const done = i < active;
      const now = i === active;
      return (
        <li key={i} className="flex items-start gap-2.5 text-xs">
          <span className={`w-5 h-5 rounded-full grid place-items-center shrink-0 mt-px
            text-[10px] font-black
            ${done ? 'bg-[#2ecf6e]/15 text-[#2ecf6e]'
                   : now ? 'bg-[#B059E8]/20 text-[#c98bf0] ring-1 ring-[#B059E8]/40'
                         : 'bg-[#15171a] border border-[#2A2D31] text-zinc-500'}`}>
            {done ? <Check className="w-3 h-3" strokeWidth={3} /> : i + 1}
          </span>
          <span className={done ? 'text-zinc-500 line-through decoration-zinc-700'
                                : now ? 'text-white font-semibold' : 'text-zinc-400'}>
            {label}
          </span>
        </li>
      );
    })}
  </ol>
);

/* ------------------------------------------------------------- ANA PANEL */
export const Mm2DepositPanel = ({ user, token, cfg, onDone, onBack }) => {
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [name, setName] = useState('');
  const [starting, setStarting] = useState(false);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  /* Kayıtlı Roblox adı öncelikli — bağlı hesabı olan kullanıcıya site adını
     varsayılan vermek yanlış olurdu. */
  useEffect(() => {
    if (name) return;
    setName(String(user?.roblox_username || user?.site_username || '').slice(0, 20));
  }, [user?.roblox_username, user?.site_username]); // eslint-disable-line

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/game/mm2/session`, auth);
      setSession(r.data?.session || null);
    } catch { /* ağ hatası paneli kilitlemesin */ }
    finally { setLoading(false); }
  }, [token]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!session) return undefined;
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
  }, [session, load]);

  const prev = useRef(null);
  useEffect(() => {
    const open = !!session;
    if (prev.current === true && !open) onDone?.();
    prev.current = open;
  }, [session]); // eslint-disable-line

  const step = session?.active_step;
  const left = useCountdown(step?.join_deadline);
  /* ⚠️ SÜRE DOLDU KARARI SUNUCUDAN (`join_expired`): istemci saati yanlış
     ayarlıysa yerel hesap yanlış sonuç verir. Yerel sayaç yalnızca yedek. */
  const expired = step ? (step.join_expired || left === 0) : false;

  const start = async () => {
    const rbx = name.trim();
    if (!RBX_RE.test(rbx)) { toast.error('Enter a valid Roblox username.'); return; }
    setStarting(true);
    try {
      /* mode:'balance' AÇIKÇA — sunucunun varsayılanı 'items' (envantere
         yatırma) ve o bilinçli bir fail-safe. */
      const r = await axios.post(`${API}/game/mm2/deposit`,
        { roblox_username: rbx, mode: 'balance' }, auth);
      if (r.data?.success) {
        /* ⚠️ MODU HEMEN KAYDET. Sunucunun oturum ucu yalnızca
           `type: "deposit"` diyor; hangi ray olduğunu SÖYLEMİYOR. Bu satır
           olmadan MM2 coinflip sayfası bu işlemi kendi eşya yatırması
           sanıp oyuncunun önüne "Deposit in progress" penceresi açıyor ve
           coinflip akışını kilitliyordu — oysa bu, MM Coin yatırması.
           Sunucu yanıtındaki `mode`'a güveniyoruz, kendi gönderdiğimize
           değil: kural sunucuda ve owner hesaplarında mod değişebiliyor. */
        rememberDepositMode(r.data.trade_id, r.data.mode || 'balance');
        toast.success('Trade ready');
        await load();
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not start the deposit.');
    } finally { setStarting(false); }
  };

  const cancel = async (id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/game/mm2/trade/cancel`, { trade_id: id }, auth);
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not cancel.');
      load();
    } finally { setBusy(false); }
  };

  const railOn = cfg?.balance_rail?.enabled !== false;
  const rate = rateLabel(cfg);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-600" />
      </div>
    );
  }

  /* ═══════════════════════════════ AKTİF İŞLEM ═════════════════════════ */
  if (step) {
    const activeStep = step.status === 'queued' ? 0 : 1;
    return (
      <div className="space-y-4" data-testid="mm2-dep-active">
        {/* ⚠️ Aktif takasta geri butonu YOK: kullanıcıyı ray seçimine geri
            göndermek, bota gitmesi gerektiğini unutturur. */}
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <h3 className="font-heading font-bold text-white text-[19px] leading-none">
              {expired ? 'Trade expired' : 'Send the trade'}
            </h3>
            <p className="text-[10px] text-zinc-500 mt-1 truncate">
              as {step.roblox_username}
            </p>
          </div>
          {!expired && left != null && (
            <span className={`shrink-0 mm-num font-bold text-lg
              ${left <= 30 ? 'text-[#E5484D]' : 'text-white'}`}>
              {mmss(left)}
            </span>
          )}
        </div>

        {/* ⚠️ SÜRE DOLDUĞUNDA DONMUŞ 0:00 GÖSTERİLMEZ. Sunucunun temizleme
            süresi bekleme penceresinden uzun; arada kullanıcı ne olduğunu
            bilmeden bekliyordu. Artık ne olduğu ve ne yapacağı yazıyor. */}
        {expired ? (
          <div className="rounded-2xl border border-[#2A2D31] bg-[#15171a] p-5 text-center
                          space-y-3">
            <Clock className="w-7 h-7 text-zinc-600 mx-auto" />
            <p className="text-sm text-zinc-300 leading-relaxed">
              The bot stopped waiting. Nothing was taken from your account.
            </p>
            <button onClick={() => cancel(step.trade_id)} disabled={busy}
              data-testid="mm2-dep-retry"
              className="w-full py-3.5 rounded-xl font-heading font-bold text-sm uppercase
                         tracking-wider bg-gradient-to-r from-[#7b2fc4] to-[#B059E8]
                         hover:from-[#B059E8] hover:to-[#c98bf0] text-white transition-all
                         shadow-lg transform active:scale-95 disabled:opacity-50
                         focus-visible:outline-none focus-visible:ring-2
                         focus-visible:ring-white/70
                         inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <RotateCcw className="w-4 h-4" />}
              Start over
            </button>
          </div>
        ) : (
          <>
            <div className="rounded-2xl border border-[#2A2D31] bg-[#15171a] p-4 space-y-3">
              <div className="flex items-center gap-2.5">
                <span className={`w-2 h-2 rounded-full shrink-0 ${step.bot_online
                  ? 'bg-[#2ecf6e] shadow-[0_0_8px_#2ecf6e]' : 'bg-zinc-600'}`} />
                <span className="font-bold text-white text-sm truncate flex-1">
                  {step.bot_roblox || step.bot_name}
                </span>
                {step.leg_total > 1 && (
                  <span className="text-[10px] font-black text-zinc-400 shrink-0">
                    {(step.leg_index || 0) + 1}/{step.leg_total}
                  </span>
                )}
              </div>

              {step.join_link ? (
                <a href={step.join_link} target="_blank" rel="noopener noreferrer"
                  data-testid="mm2-dep-join"
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl
                             font-heading font-bold text-sm uppercase tracking-wider
                             bg-gradient-to-r from-[#7b2fc4] to-[#B059E8]
                             hover:from-[#B059E8] hover:to-[#c98bf0] text-white
                             transition-all shadow-lg transform active:scale-95
                             focus-visible:outline-none focus-visible:ring-2
                             focus-visible:ring-white/70">
                  <ExternalLink className="w-4 h-4" /> Join server
                </a>
              ) : (
                <p className="text-xs text-amber-400 text-center py-2">
                  No server link — contact support.
                </p>
              )}
            </div>

            <StepList active={activeStep} />

            {step.status === 'queued' && (
              <button onClick={() => cancel(step.trade_id)} disabled={busy}
                data-testid="mm2-dep-cancel"
                className="w-full py-2.5 text-xs font-bold text-zinc-500 hover:text-rose-400
                           transition-colors inline-flex items-center justify-center gap-1.5">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <X className="w-3.5 h-3.5" />}
                Cancel
              </button>
            )}
          </>
        )}
      </div>
    );
  }

  /* ═══════════════════════════════ BAŞLANGIÇ ═══════════════════════════ */
  return (
    <div className="space-y-5" data-testid="mm2-dep-start">
      {/* ⚠️ GERİ + BAŞLIK + KUR TEK SATIRDA. Çekim panelindeki düzenin
          aynısı: geri butonu modalın gövdesinde ayrı bir satır işgal
          etmiyor, başlığın soluna oturuyor. İki panel arasında gezinen
          kullanıcı aynı iskeleti görüyor. */}
      <div className="flex items-center gap-3">
        {onBack && <BackButton onClick={onBack} testId="mm2-dep-back" />}
        <div className="min-w-0 flex-1">
          <h3 className="font-heading font-bold text-white text-[19px] leading-none truncate">
            Deposit MM2 items
          </h3>
          <p className="text-[10px] text-zinc-500 mt-1 truncate">
            Trade your items to our bot for MM Coin.
          </p>
        </div>
        {/* ⚠️ BAKİYE YATIRMA EKRANINDA DA GÖRÜNÜYOR — çekimdekiyle AYNI
            biçimde ve aynı yerde. Kullanıcı "yatırmadan önce ne kadardı,
            sonra ne oldu" karşılaştırmasını gözünü kaydırmadan yapabilsin
            diye; ayrıca cüzdan geçiş düğmeleri kaldırıldıktan sonra bu
            bilgi başka hiçbir yerde durmuyor. */}
        <div className="text-right shrink-0">
          <p className="mm-num font-bold text-white text-[15px] inline-flex items-center
                        gap-1.5 leading-none">
            {fmtFull(user?.balance || 0)}<MmCoinIcon size={14} />
          </p>
          <p className="mm-eyebrow mt-1.5">Balance</p>
        </div>
      </div>

      {!railOn && (
        <div className="rounded-xl border border-amber-500/25 bg-amber-500/10 px-3.5 py-3
                        flex items-center gap-2.5">
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
          <p className="text-xs text-amber-100/85">Deposits are paused right now.</p>
        </div>
      )}

      <AccountField value={name} onChange={setName}
        defaultName={user?.roblox_username || user?.site_username}
        disabled={!railOn || starting} />

      {/* ⚠️ KUR TEK SATIR. Önceki hâli ayrı bir karttı ve ekranın üçte birini
          kaplıyordu; kullanıcının bakmadığı bir teknik detay için fazla yer. */}
      {rate && (
        <div className="mm-inset flex items-center justify-between px-3.5 py-2.5">
          <span className="mm-eyebrow">Rate</span>
          <span className="mm-num text-[12px] font-bold text-[#2ecf6e] inline-flex items-center gap-1.5">
            1 item value → {rate} <MmCoinIcon size={12} />
          </span>
        </div>
      )}

      <button onClick={start}
        disabled={!railOn || starting || !RBX_RE.test(name.trim())}
        data-testid="mm2-dep-start-btn"
        className="w-full py-4 rounded-xl font-heading font-bold text-lg uppercase
                   tracking-wider transition-all shadow-lg bg-gradient-to-r
                   from-[#7b2fc4] to-[#B059E8] hover:from-[#B059E8] hover:to-[#c98bf0]
                   text-white disabled:opacity-50 disabled:grayscale transform
                   active:scale-95 focus-visible:outline-none focus-visible:ring-2
                   focus-visible:ring-white/70 inline-flex items-center justify-center gap-2">
        {starting ? <><Loader2 className="w-5 h-5 animate-spin" /> Creating…</>
                  : 'Start deposit'}
      </button>
    </div>
  );
};

export default Mm2DepositPanel;